"use client";

import { useState } from "react";
import { getSiteContent } from "@/content";
import { type Locale, localizedPath } from "@/lib/site";
import { StatusBadge } from "@/components/ui/StitchUI";

export function TrackingPage({ locale }: { locale: Locale }) {
  const t = getSiteContent(locale).pages.tracking;
  const [query, setQuery] = useState<string>(t.initialNo);
  const [trackingNo, setTrackingNo] = useState<string>(t.initialNo);

  return (
    <main className="min-h-screen px-margin-mobile pb-section-gap pt-[156px] md:px-margin-desktop md:pt-[168px]">
      <div className="mx-auto max-w-container-max">
        <header className="mb-stack-lg text-center">
          <h1 className="mb-4 font-display-lg text-display-lg-mobile text-primary md:text-display-lg">{t.title}</h1>
          <p className="mx-auto max-w-2xl font-body-lg text-body-lg text-on-surface-variant">{t.subtitle}</p>
        </header>

        <section className="mx-auto mb-stack-lg max-w-3xl">
          <div className="glass-card flex flex-col items-stretch gap-4 rounded-xl border border-border-pale p-2 shadow-sm ring-1 ring-black/5 md:flex-row">
            <div className="relative flex-grow">
              <span className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 w-6 -translate-y-1/2 overflow-hidden text-[24px] leading-none text-outline">search</span>
              <input
                className="tracking-search-input w-full border-none bg-transparent py-4 pl-12 pr-4 font-body-md text-primary placeholder:text-outline/60 focus:outline-none focus:ring-0"
                placeholder={t.searchPlaceholder}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <button className="flex items-center justify-center gap-2 rounded-xl bg-parcel-orange px-10 py-4 font-label-md text-white shadow-md shadow-parcel-orange/20 transition-all hover:brightness-105 active:scale-95" onClick={() => setTrackingNo(query.trim() || t.fallbackNo)} type="button">
              <span className="material-symbols-outlined">local_shipping</span>
              {t.searchButton}
            </button>
          </div>
          <p className="mt-4 text-center font-label-sm text-outline">{t.commonSearch}</p>
        </section>

        <div className="mb-stack-lg grid grid-cols-1 gap-gutter lg:grid-cols-12">
          <section className="rounded-xl border border-border-pale bg-white p-stack-lg shadow-sm lg:col-span-8">
            <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-start">
              <div>
                  <StatusBadge className="mb-2 gap-1.5 bg-secondary-container/20 px-3 py-1 text-secondary">
                    <span className="material-symbols-outlined text-[18px]">package_2</span>
                    {t.statusBadge}
                  </StatusBadge>
                <h2 className="font-headline-lg text-headline-lg text-primary">{t.trackingNoLabel} {trackingNo}</h2>
              </div>
              <div className="text-left md:text-right">
                <p className="mb-1 font-label-sm uppercase tracking-wider text-outline">{t.estimatedDeliveryLabel}</p>
                <p className="font-headline-md text-headline-md text-secondary">{t.estimatedDeliveryValue}</p>
              </div>
            </div>

            <div className="tracking-timeline-scroll no-scrollbar relative overflow-x-auto pb-12 pt-8">
              <div className="tracking-timeline-track px-4">
                <div className="tracking-timeline-line absolute left-8 right-8 top-[52px] h-1 rounded-full bg-surface-container-highest" />
                <div className="tracking-timeline-progress timeline-gradient animated-flow absolute left-8 top-[52px] h-1 w-[65%] rounded-full" />
                <div className="tracking-timeline-steps relative flex justify-between">
                  {t.steps.map(([icon, label, en, state]) => (
                    <div className={`tracking-step flex w-24 flex-col items-center gap-4 ${state === false ? "opacity-40" : ""}`} key={label as string}>
                      <div
                        className={
                          state === "active"
                            ? "tracking-step-node tracking-step-node-active z-10 flex h-12 w-12 scale-125 items-center justify-center rounded-full border-4 border-secondary bg-white shadow-xl ring-8 ring-secondary/10"
                            : state
                              ? "tracking-step-node z-10 flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-white shadow-lg shadow-secondary/30"
                              : "tracking-step-node z-10 flex h-10 w-10 items-center justify-center rounded-full bg-surface-container-highest"
                        }
                      >
                        <span className={`material-symbols-outlined text-[20px] ${state === "active" ? "font-bold text-secondary" : state ? "" : "text-outline"}`}>{icon}</span>
                      </div>
                      <span className={`text-center ${state === "active" ? "mt-2 font-label-md font-bold text-secondary" : state ? "font-label-sm text-primary" : "font-label-sm text-outline"}`}>
                        {label}
                        <br />
                        <span className="font-label-sm text-label-sm uppercase opacity-70">{en}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <aside className="grid grid-cols-1 gap-gutter lg:col-span-4">
            <section className="ambient-deep-bg relative overflow-hidden rounded-xl bg-primary p-stack-lg text-white shadow-lg">
              <div className="absolute -right-8 -top-8 scale-150 opacity-10">
                <span className="material-symbols-outlined text-[120px]">anchor</span>
              </div>
              <h2 className="relative z-10 mb-6 font-headline-md text-headline-md">{t.overview.title}</h2>
              <ul className="relative z-10 space-y-4">
                {t.overview.rows.map(([label, value, highlight]) => (
                  <Summary label={label} value={value} highlight={highlight} key={label} />
                ))}
              </ul>
            </section>

            <section className="rounded-xl border border-border-pale bg-white p-stack-lg shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-glacier-white text-secondary">
                  <span className="material-symbols-outlined">support_agent</span>
                </div>
                <div>
                  <h3 className="font-label-md text-primary">{t.help.title}</h3>
                  <p className="font-label-sm text-label-sm text-outline">{t.help.copy}</p>
                </div>
              </div>
              <a className="block w-full rounded-lg border border-secondary/20 bg-secondary/5 py-3 text-center font-label-md text-secondary transition-all hover:bg-secondary hover:text-white" href={localizedPath(locale, "contact")}>
                {t.help.action}
              </a>
            </section>
          </aside>
        </div>

        <section className="grid grid-cols-1 gap-gutter lg:grid-cols-12">
          <div className="overflow-hidden rounded-xl border border-border-pale bg-white shadow-sm lg:col-span-8">
            <div className="flex items-center justify-between border-b border-border-pale bg-surface-container-low/30 px-stack-lg py-4">
              <h2 className="font-headline-md text-headline-md text-primary">{t.detailTitle}</h2>
              <button className="flex items-center gap-1 font-label-sm text-secondary" type="button">
                <span className="material-symbols-outlined text-[18px]">download</span>
                {t.exportLabel}
              </button>
            </div>
            <div className="p-stack-lg">
              <div className="space-y-8">
                {t.logs.map(([time, date, title, desc], index) => (
                  <div className="relative pl-8 before:absolute before:bottom-[-32px] before:left-[11px] before:top-2 before:w-[2px] before:bg-border-pale before:content-[''] last:before:hidden" key={title}>
                    <div className={`absolute left-0 top-1.5 z-10 h-6 w-6 rounded-full border-4 border-white ${index === 0 ? "bg-secondary shadow-sm" : "bg-surface-container-highest"}`} />
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-8">
                      <div className="min-w-[120px]">
                        <p className={`font-label-md ${index === 0 ? "text-primary" : "text-on-surface-variant"}`}>{time}</p>
                        <p className="font-label-sm text-label-sm text-outline">{date}</p>
                      </div>
                      <div className="flex-grow">
                        <p className={`mb-1 font-body-md ${index === 0 ? "font-semibold text-primary" : "text-on-surface-variant"}`}>{title}</p>
                        <p className="text-body-sm text-outline">{desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-gutter lg:col-span-4">
            <section className="h-fit overflow-hidden rounded-xl border border-border-pale bg-white shadow-sm">
              <div className="relative aspect-square w-full">
                <div className="h-full w-full bg-cover bg-center" style={{ backgroundImage: "url('/assets/images/route-map.png')" }} />
                <div className="glass-card absolute bottom-4 left-4 right-4 rounded-lg border border-white/20 p-3 shadow-xl">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
                    <p className="font-label-sm text-label-sm text-primary">{t.map.positionLabel}</p>
                  </div>
                </div>
              </div>
              <div className="p-stack-lg">
                <h3 className="mb-2 font-label-md text-primary">{t.map.destinationTitle}</h3>
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-secondary">location_on</span>
                  <p className="font-body-sm text-on-surface-variant">{t.map.destination}</p>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-border-pale bg-surface-container-low p-stack-lg">
              <h3 className="mb-4 flex items-center gap-2 font-label-md text-primary">
                <span className="material-symbols-outlined text-[20px]">notifications_active</span>
                {t.notifications.title}
              </h3>
              <div className="space-y-4">
                {t.notifications.toggles.map(([label, active]) => (
                  <Toggle label={label} active={active} key={label} />
                ))}
              </div>
            </section>
          </aside>
        </section>
      </div>
    </main>
  );
}

function Summary({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <li className="flex justify-between border-b border-white/10 pb-2">
      <span className="font-body-sm opacity-60">{label}</span>
      <span className={`font-label-md ${highlight ? "text-tertiary-fixed-dim" : ""}`}>{value}</span>
    </li>
  );
}

function Toggle({ label, active = false }: { label: string; active?: boolean }) {
  return (
    <label className="group flex cursor-pointer items-center justify-between">
      <span className="text-body-sm text-on-surface-variant transition-colors group-hover:text-primary">{label}</span>
      <span className={`relative h-5 w-10 rounded-full ${active ? "bg-secondary" : "bg-outline-variant"}`}>
        <span className={`absolute top-1 h-3 w-3 rounded-full bg-white ${active ? "right-1" : "left-1"}`} />
      </span>
    </label>
  );
}
